import { action, observable, reaction, makeObservable } from 'mobx';
import RouteRecognizer from 'route-recognizer';
import { LoginPage } from '../page/auth/login.page';
import { MissingPage } from '../page/missing.page';
import type { IkeLinkConfig, IkePage, IkePageConfig, IkePageName } from '../page/page';
import { PageLayout, PageLayoutState } from '../page/page.layout';
import { type IkePageEntry, PageRegistry } from '../page/page.registry';
import { type IkeClientService, Logger, Services } from './index';

export const ROUTE_PREFIX = '#';

export type RouteParams = { [name: string]: string };

export class RouteService implements IkeClientService {
	__priority = 100;

	private router: RouteRecognizer;
	private layout: PageLayout;

	private hasLoaded: boolean = false;
	@observable tokensLoaded: boolean = false;
	@observable profilesLoaded: boolean = false;

	params = observable.map<string, string>({}, { deep: false });

	constructor() {
		makeObservable(this);
	}

	init() {
		reaction(
			() => this.tokensLoaded,
			() => this.firstLoad()
		);
		reaction(
			() => this.profilesLoaded,
			() => this.firstLoad()
		);
	}

	bindRoutes() {
		this.router = new RouteRecognizer();
		for (const pageEntry of PageRegistry.pages) {
			const paths = Array.isArray(pageEntry.config.path) ? pageEntry.config.path : [pageEntry.config.path];
			for (const path of paths) {
				this.router.add([{ path, handler: pageEntry }]);
			}
		}

		this.layout = new PageLayout();

		Services.Render.mount(Services.Window.document.body, this.layout, 'root');

		Services.Window.window.addEventListener('hashchange', (ev: HashChangeEvent) => this.resolveRoute(ev.newURL));
	}

	// Once we check department tokens and get the user profile we resolve the route
	firstLoad() {
		if (this.tokensLoaded && this.profilesLoaded && !this.hasLoaded) {
			this.hasLoaded = true;
			this.resolveRoute(window.location.href);
		}
	}

	@action departmentReset() {
		this.tokensLoaded = false;
		this.profilesLoaded = false;
		this.hasLoaded = false;
	}

	get active(): IkePage {
		return this.layout != null ? this.layout.page : null;
	}

	supportDepartmentChange() {
		return this.active != null && this.active.config.supportDepartmentChange;
	}

	setAllParams(params: RouteParams, history?: boolean) {
		for (const key of Object.keys(params)) {
			if (this.params.get(key) !== params[key]) {
				this.params.merge(params);
				this.applyParams(history);
				return;
			}
		}
	}

	setParam(key: string, value: string, history?: boolean) {
		if (this.params.get(key) !== value) {
			this.params.set(key, value);
			this.applyParams(history);
		}
	}

	deleteParam(key: string, history?: boolean) {
		if (this.params.has(key)) {
			this.params.delete(key);
			this.applyParams(history);
		}
	}

	openNewWindow(name: IkePageName, params?: RouteParams) {
		const url = this.getRouteURL(name, params);
		Services.Window.window.open(url, '_blank');
	}

	getRouteURL(name: IkePageName, params?: RouteParams): string {
		const config = PageRegistry.getConfig(name);
		if (config == null) {
			return undefined;
		}

		return this.compileURL(config, params);
	}

	go(name: IkePageName, params?: RouteParams) {
		const url = this.getRouteURL(name, params);
		this.goRaw(url);
	}

	goRaw(url: string) {
		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Route, 'route', url, 1);
		Logger.info({ url }, 'Routing');
		Services.Window.window.location.href = url;
	}

	isPoleLoading() {
		// return this.route[1] === this.PAGE.dashboard.poleLoading;
		return true;
	}

	isPoleStatus() {
		// return this.route[1] === this.PAGE.dashboard.poleStatus;
		return true;
	}
	private applyParams(history?: boolean) {
		const $window = Services.Window.window;
		if ($window == null) {
			return;
		}

		const state: null = null;
		const title: null = null;

		const path = this.compileURL(this.active.config, Object.fromEntries(this.params));

		if (typeof $window.history.pushState === 'function') {
			if (history) {
				$window.history.pushState(state, title, path);
			} else {
				$window.history.replaceState(state, title, path);
			}
		} else {
			$window.location.href = path;
		}
	}

	private compileURL(config: IkeLinkConfig, params?: RouteParams) {
		if (config.type === 'external') {
			return config.path;
		}

		let url = this.getMatchingPath(config, params);

		let paramsString = '';
		if (params != null && Object.keys(params).length > 0) {
			// Replace path params (/map/:location)
			const args: { [key: string]: string } = {};
			for (const key of Object.keys(params)) {
				if (params[key] != null) {
					args[key] = params[key];
				}
			}

			url = url.replace(/:([^/]+)/g, (match, token) => {
				const value = args[token];
				delete args[token];
				return value;
			});

			// Build query string (&foo=bar)
			if (Object.keys(args).length > 0) {
				paramsString =
					'?' +
					Object.keys(args)
						.map(
							(key) =>
								`${Services.Window.window.encodeURIComponent(key)}=${Services.Window.window.encodeURIComponent(args[key])}`
						)
						.join('&');
			}
		}

		return `/${ROUTE_PREFIX}${url}${paramsString}`;
	}

	private getMatchingPath(config: IkePageConfig, params?: RouteParams): string {
		const paths = Array.isArray(config.path) ? config.path : [config.path];
		params = params || {};

		let matchingPath = paths[0];
		let matchCount = 0;

		for (let i = 0; i < paths.length; i++) {
			const args = paths[i].match(/:([^/]+)/g) || [];

			let argMatchCount = 0;
			for (let arg of args) {
				if (params[arg.substr(1)] != null) {
					argMatchCount += 1;
				}
			}

			if (argMatchCount > matchCount) {
				matchingPath = paths[i];
				matchCount = argMatchCount;
			}
		}

		return matchingPath;
	}

	@action private resolveRoute(url: string) {
		let path = url;
		if (!url.startsWith('/')) {
			if (url.includes(ROUTE_PREFIX)) {
				path = url.split(ROUTE_PREFIX).pop();
			} else {
				path = '/';
			}
		}

		const results = this.router.recognize(path);
		let page: IkePage;
		let params = {};

		if (results == null || results.length === 0) {
			page = new MissingPage();
		} else {
			const result = results[0];
			const pageEntry = result.handler as IkePageEntry;

			if (pageEntry.config.auth.required && !Services.User.isAuthenticated) {
				page = new LoginPage(`/${ROUTE_PREFIX}${path}`);
			} else if (
				// Check if the user has the correct tokens and/or user roles to access the requested page
				(pageEntry.config.auth.tokens && !Services.Token.hasAll(pageEntry.config.auth.tokens)) ||
				(pageEntry.config.auth.userLevel &&
					!Services.UserProfile.isOneOf(
						Services.UserProfile.get(Services.User.current.objectId),
						pageEntry.config.auth.userLevel
					)) ||
				(pageEntry.config.auth.userLevelBlackList &&
					Services.UserProfile.isOneOf(
						Services.UserProfile.get(Services.User.current.objectId),
						pageEntry.config.auth.userLevelBlackList
					))
			) {
				page = new MissingPage(403);
			} else {
				page = new pageEntry.page();
			}

			params = { ...result.params, ...results.queryParams };
		}

		var previousPage = this.layout.page;

		if (!previousPage || (previousPage && previousPage.config.name !== page.config.name)) {
			this.layout.setPage(page);
			this.params.replace(params);

			if (previousPage && this.layout.page !== previousPage) {
				previousPage.unload();
			}
		}

		PageLayoutState.nav.setVisible(page.config.layout.nav);
	}
}
